import { useMemo } from 'react';
import { DropdownOption } from '../App/types';

export function useApplicationStatusOptions(
  applicationStatuses: Array<DropdownOption>,
  applicationStatusId: string,
  disableWaitlistOption: boolean,
  filterOptionsWaitlist: boolean,
  waitlistTooltipText: string,
  hasReinstatePermission: boolean = true,
): [Array<DropdownOption>] {
  const selectedApplicationStatus = applicationStatuses.find(
    (applicationStatus) => applicationStatus.value === applicationStatusId,
  );

  const applicationStatusOptions = useMemo(() => {
    return applicationStatuses
      .filter((applicationStatus) => {
        if (selectedApplicationStatus?.text === 'Abandoned') {
          return ['New Portal App', 'Abandoned'].includes(
            applicationStatus.text,
          );
        }

        if (
          selectedApplicationStatus?.text !== 'New Portal App' &&
          applicationStatus.text === 'Abandoned'
        ) {
          return false;
        }

        return true;
      })
      .map((applicationStatus) => {
        // Special case for Waitlist - disable if no reinstate permission when coming from Canceled or Denied
        if (
          applicationStatus.text === 'Waitlist' &&
          ['Canceled', 'Denied'].includes(selectedApplicationStatus?.text) &&
          !hasReinstatePermission
        ) {
          return {
            ...applicationStatus,
            disabled: true,
            tooltip: 'Permission Required',
          };
        }

        // Original logic for other cases
        return ['Waitlist'].includes(applicationStatus.text) &&
          disableWaitlistOption
          ? {
              ...applicationStatus,
              disabled: true,
              ...(waitlistTooltipText
                ? {
                    tooltip: waitlistTooltipText,
                  }
                : {}),
            }
          : applicationStatus;
      });
  }, [
    applicationStatuses,
    disableWaitlistOption,
    selectedApplicationStatus,
    waitlistTooltipText,
    hasReinstatePermission, // Add to dependency array
  ]);

  if (!selectedApplicationStatus) {
    return [applicationStatusOptions];
  }

  if (!filterOptionsWaitlist) {
    return [applicationStatusOptions];
  }

  const statusToFilterMap: Record<string, string[]> = {
    Waitlist: ['Canceled', 'Denied', 'Waitlist'],
    'In Process': ['Canceled', 'Denied', 'Approved', 'In Process'],
    Canceled: ['Waitlist', 'Canceled'],
    Denied: ['Waitlist', 'Denied'],
    Approved: ['Canceled', 'Denied', 'In Process', 'Approved'],
    'New Portal App': ['Waitlist', 'Abandoned', 'New Portal App'],
    Abandoned: ['Abandoned', 'New Portal App'],
  };

  const filteredOptions =
    statusToFilterMap[selectedApplicationStatus.text] || [];

  return [
    applicationStatusOptions.filter((item) =>
      filteredOptions.includes(item.text),
    ),
  ];
}
